import { Box, Divider, Group } from '@byecode/ui'
import type { VariableADTvalue } from '@lighthouse/core'
import { EVENT_VARIABLE_TYPE, type PageAbstract, type RichTextContentProtocol } from '@lighthouse/core'
import { fieldConvertValue, generateText } from '@lighthouse/shared'
import React, { useCallback, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import { AnchorSetting } from '@/components/AnchorSetting'
import { CollectionEvent } from '@/components/CollectionEvent'
import { EventList } from '@/components/EventList'
import { getBlockAndPageDesignLimit, PositionSetting } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useBlockSettingContext } from '@/contexts/BlockSettingContext'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import { FloatingControl } from '../Common/FloatingControl'
import FormSegmentedControl from '../Common/FormSegmentedControl'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'
import { ButtonConfigure } from './ButtonConfigure'
import { BUTTON_RADIUS_OPTIONS, BUTTON_SHAPE_OPTIONS, BUTTON_SIZE_OPTIONS } from './constant'

interface ButtonSettingProps {
    allPages: PageAbstract[]
}

const SCxContainer = styled.div`
    height: 100%;
`

const buttonEventTypes = [EVENT_VARIABLE_TYPE.BUTTON_CLICK]

export const ButtonSetting: React.FC<ButtonSettingProps> = ({ allPages }) => {
    const { control, watch } = useFormContext()
    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)
    const [name, shape] = watch(['name', 'shape'])
    const styleProps = getBlockAndPageDesignLimit('button')
    const { blockId } = useBlockSettingContext()

    const parseVariable = useParseVariableWithSubscription(name)
    const parseVariable2String = useCallback(
        (value: VariableADTvalue) => {
            const field = parseVariable(value)
            if (!field) {
                return ''
            }

            return fieldConvertValue(field)
        },
        [parseVariable]
    )
    const handleRenderTitle = useCallback(
        (v: RichTextContentProtocol) => {
            return generateText(v, { variable: { renderLabel: v => parseVariable2String(v.attrs.value) } })
        },
        [parseVariable2String]
    )

    return (
        <Box style={{ height: '100%' }}>
            <BlockConfigureSegmentedControl isInterAction value={currentTab} onChange={setCurrentTab} />
            <SCxContainer key={BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN}>
                <PositionSetting {...styleProps.position} />
                <Divider />
                <SizeSetting {...styleProps.size} />
                <Divider />
                <Group label="按钮">
                    <ButtonConfigure style={{ padding: 0 }} />
                </Group>
                <Divider />
                <Group label="样式">
                    <Controller
                        name="shape"
                        control={control}
                        render={({ field }) => (
                            <FormSegmentedControl
                                label="风格"
                                size="md"
                                data={BUTTON_SHAPE_OPTIONS}
                                value={field.value}
                                onChange={field.onChange}
                            />
                        )}
                    />

                    {shape === 'rectangle' && (
                        <Controller
                            name="radius"
                            control={control}
                            render={({ field }) => (
                                <FormSegmentedControl
                                    label="圆角"
                                    size="md"
                                    data={BUTTON_RADIUS_OPTIONS}
                                    value={field.value}
                                    onChange={v => field.onChange(v)}
                                />
                            )}
                        />
                    )}

                    <Controller
                        name="size"
                        control={control}
                        render={({ field }) => (
                            <FormSegmentedControl
                                label="尺寸"
                                size="md"
                                data={BUTTON_SIZE_OPTIONS}
                                value={field.value}
                                onChange={v => field.onChange(v)}
                            />
                        )}
                    />
                </Group>
                <Divider />
                <Controller name="cursor" render={({ field }) => <CursorSetting {...field} />} />
            </SCxContainer>

            <SCxContainer key={BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION}>
                <Controller
                    name="eventList"
                    control={control}
                    render={({ field }) => <EventList blockType="button" value={field.value} onChange={field.onChange} />}
                />

                <Divider />
                <Controller
                    name="floatPointerList"
                    control={control}
                    render={({ field }) => {
                        return <FloatingControl value={field.value} blockId={blockId} onChange={field.onChange} />
                    }}
                />
                <Divider />
                <AnimationSetting />
            </SCxContainer>
            <SCxContainer key={BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE} hidden={currentTab !== BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE}>
                <Controller
                    control={control}
                    name="collectEvents"
                    render={({ field }) => <CollectionEvent eventTypes={buttonEventTypes} value={field.value} onChange={field.onChange} />}
                />
                <Divider />
                <AnchorSetting />
                <Divider />
                <VisibilitySetting />
            </SCxContainer>
        </Box>
    )
}
